import { Notification } from "@/utils/notificationUtils";
import { openBlobFile } from "@/utils/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { debtAccountApi } from "../../../modules/commissions/debtaccount/api";
import {
  CreateUpdateDebtAccountMovement,
  DebtAccountMovementFilterPageRequest,
  DebtAccountsReportFilterExportRequest,
  DebtAccountsReportFilterPageRequest,
} from "../../../modules/commissions/debtaccount/types";
import { EntityObject, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../types";

export const DEBT_ACCOUNT_QUERY_KEYS = {
  getDebtAccountsReport: (payload?: DebtAccountsReportFilterPageRequest) =>
    payload ? ["getDebtAccountsReport", payload] : ["getDebtAccountsReport"],
  downloadDebtAccountsExport: () => ["downloadDebtAccountsExport"],
  filterDebtAccountMovements: (payload?: EntityObject<DebtAccountMovementFilterPageRequest>) =>
    payload ? ["filterDebtAccountMovements", payload] : ["filterDebtAccountMovements"],
  createManualDebtAccountMovement: () => ["createManualDebtAccountMovement"],
  updateManualDebtAccountMovement: () => ["updateManualDebtAccountMovement"],
  deleteManualDebtAccountMovement: () => ["deleteManualDebtAccountMovement"],
};

export const useGetDebtAccountsReport = (payload: DebtAccountsReportFilterPageRequest) => {
  return useQuery({
    queryKey: DEBT_ACCOUNT_QUERY_KEYS.getDebtAccountsReport(payload),
    queryFn: async () => {
      const { data } = await debtAccountApi.getDebtAccountsReport(payload);

      return data;
    },
  });
};

export const useDownloadDebtAccountsExport = () => {
  return useMutation({
    mutationKey: DEBT_ACCOUNT_QUERY_KEYS.downloadDebtAccountsExport(),
    mutationFn: async (payload: DebtAccountsReportFilterExportRequest) => {
      const response = await debtAccountApi.downloadDebtAccountsExport(payload);
      openBlobFile(response);

      return response.data;
    },
  });
};

export const useFilterDebtAccountMovements = (payload: EntityObject<DebtAccountMovementFilterPageRequest>) => {
  return useQuery({
    queryKey: DEBT_ACCOUNT_QUERY_KEYS.filterDebtAccountMovements(payload),
    queryFn: async () => {
      const { data } = await debtAccountApi.filterDebtAccountMovements(payload);

      return data;
    },
  });
};

export const useCreateManualDebtAccountMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DEBT_ACCOUNT_QUERY_KEYS.createManualDebtAccountMovement(),
    mutationFn: async (payload: EntityObject<CreateUpdateDebtAccountMovement>) => {
      const { data } = await debtAccountApi.createManualDebtAccountMovement(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEBT_ACCOUNT_QUERY_KEYS.filterDebtAccountMovements() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateManualDebtAccountMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DEBT_ACCOUNT_QUERY_KEYS.updateManualDebtAccountMovement(),
    mutationFn: async (payload: TwoLevelEntityObject<CreateUpdateDebtAccountMovement>) => {
      const { data } = await debtAccountApi.updateManualDebtAccountMovement(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEBT_ACCOUNT_QUERY_KEYS.filterDebtAccountMovements() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteManualDebtAccountMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DEBT_ACCOUNT_QUERY_KEYS.deleteManualDebtAccountMovement(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return debtAccountApi.deleteManualDebtAccountMovement(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEBT_ACCOUNT_QUERY_KEYS.filterDebtAccountMovements() });
      Notification.itemDeletedNotification();
    },
  });
};
